import type { ActiveAnalysis } from "../../../api/useActiveAnalysesStream";
import type { Setup } from "../../../api/useSetups";
import { getAnalysisSetup } from "diagnostics-domain/setup/getSetup";
import getQuantityOffset from "../../../api/getQuantityOffset";
import getQuantitySigma from "../../../api/getQuantitySigma";
import type { MetrologyOverrides } from "../../../api/useMetrologyOverrides";
import React from "react";
import { isInPeriod, type Period } from "@ui/Highcharts/hooks/usePeriod";
import type { QuantityIndex, Quantity } from "../../../api/useQuantities";
import isSymptom from "../Symptoms/isSymptom";
import { isAnalysisQuantityValid } from "../metrology";

export interface QuantityAlert {
  isInAlert: boolean;
  inRange: (boolean | null)[];
}
export type AlertType = "measures" | "symptoms";

type AnalysisAlertsCache = WeakMap<
  ActiveAnalysis,
  {
    measures?: { [quantityId: string]: boolean };
    symptoms?: { [quantityId: string]: boolean | null };
  }
>;
const alertsCaches = new WeakMap<
  Setup[],
  WeakMap<QuantityIndex, WeakMap<MetrologyOverrides, AnalysisAlertsCache>>
>();

// this hook is massively optimized for performance, test and test again if you change its memoization patterns
export function useQuantityAlerts({
  activeAnalyses,
  period,
  selectedAnalyses,
  metrologyOverrides,
  quantityIndex,
  setups,
  alertType,
}: {
  activeAnalyses: ActiveAnalysis[];
  period: Period;
  selectedAnalyses: ActiveAnalysis[];
  metrologyOverrides: MetrologyOverrides;
  quantityIndex: QuantityIndex;
  setups: Setup[];
  alertType: "measures" | "symptoms";
}) {
  const analysisAlertCache: AnalysisAlertsCache = React.useMemo(() => {
    let setupCache = alertsCaches.get(setups);
    if (!setupCache) {
      setupCache = new WeakMap();
      alertsCaches.set(setups, setupCache);
    }

    let quantityIndexCache = setupCache.get(quantityIndex);
    if (!quantityIndexCache) {
      quantityIndexCache = new WeakMap();
      setupCache.set(quantityIndex, quantityIndexCache);
    }

    let cache = quantityIndexCache.get(metrologyOverrides);
    if (!cache) {
      cache = new WeakMap();
      quantityIndexCache.set(metrologyOverrides, cache);
    }

    return cache;
  }, [metrologyOverrides, quantityIndex, setups]);

  const inRangeAnalyses = React.useMemo(
    () => activeAnalyses.filter(({ date }) => isInPeriod(period, date)),
    [activeAnalyses, period],
  );

  // this is the performance bottleneck, we need cache the results of all quantity alerts for each analysis
  const inRangeAlerts = React.useMemo(() => {
    const quantities = Object.values(quantityIndex);
    return inRangeAnalyses.map(analysis => {
      let cache = analysisAlertCache.get(analysis);
      if (cache === undefined) {
        cache = {};
        analysisAlertCache.set(analysis, cache);
      }

      if (alertType === "measures") {
        if (cache.measures) return cache.measures;
        const result: { [quantityId: string]: boolean } = {};
        for (const quantity of quantities)
          result[quantity.id] = !isAnalysisQuantityValid(analysis, quantity);
        cache.measures = result;
        return result;
      } else {
        if (cache.symptoms) return cache.symptoms;
        const result: { [quantityId: string]: boolean | null } = {};
        for (const quantity of quantities)
          result[quantity.id] = computeSymptomAlert(analysis, quantity, metrologyOverrides, setups);
        cache.symptoms = result;
        return result;
      }
    });
  }, [alertType, inRangeAnalyses, metrologyOverrides, setups, analysisAlertCache, quantityIndex]);

  const selectedAlertsByQuantityId = React.useMemo(() => {
    const result: { [quantityId: string]: boolean } = {};
    for (const quantity of Object.values(quantityIndex)) {
      result[quantity.id] =
        alertType === "measures"
          ? selectedAnalyses.some(analysis => !isAnalysisQuantityValid(analysis, quantity))
          : selectedAnalyses.some(
              analysis =>
                computeSymptomAlert(analysis, quantity, metrologyOverrides, setups) === true,
            );
    }

    return result;
  }, [alertType, metrologyOverrides, quantityIndex, selectedAnalyses, setups]);

  const inRangeAlertsByQuantityId = React.useMemo(() => {
    const result: { [quantityId: string]: (boolean | null)[] } = {};
    for (const quantityId in quantityIndex)
      result[quantityId] = inRangeAlerts.map(alert => alert[quantityId]!);
    return result;
  }, [inRangeAlerts, quantityIndex]);

  return React.useMemo(() => {
    const result: { [quantityId: string]: QuantityAlert } = {};
    for (const quantityId in quantityIndex) {
      const isInAlert = selectedAlertsByQuantityId[quantityId]!;
      const inRange = inRangeAlertsByQuantityId[quantityId]!;
      result[quantityId] = { isInAlert, inRange };
    }
    return result;
  }, [selectedAlertsByQuantityId, inRangeAlertsByQuantityId, quantityIndex]);
}

function computeSymptomAlert(
  analysis: ActiveAnalysis,
  quantity: Quantity,
  _metrologyOverrides: MetrologyOverrides,
  setups: Setup[],
): boolean | null {
  const quantityValue = analysis.quantityValues[quantity.id];
  if (quantityValue === undefined) return null;
  if (!isAnalysisQuantityValid(analysis, quantity)) return null;
  const setup = getAnalysisSetup(setups, analysis);
  const sigma = getQuantitySigma(setup, quantity.id);
  const offset = getQuantityOffset(setup, quantity.id);

  return isSymptom(quantityValue, offset, sigma);
}
